import type {
  GoalActivityInitiative,
  GoalActivityProject,
  ViewLaneCount,
  ViewRecord,
} from '@localagent/sdk';
import type { BoardViewResponse, Ticket } from '@localagent/sdk';
import type { PositionTreeNode as OrgTreeNode } from '@localagent/sdk';
import { ROLE_DISPLAY_NAMES, occupantAgentName } from '../types/orgchart';
import { BOARD_COLUMNS, TICKET_STATE_LABELS } from './work-management-helpers';
import {
  PROJECT_LANES,
  PROJECT_LANE_LABELS,
  groupProjectsByLane,
} from './project-board-helpers';
import type { WorkItemSessionLink } from '../stores/work/atoms';

export interface SurfaceRecords {
  counts: ViewLaneCount[];
  visible: ViewRecord[];
}

export function scopeRecord(type: string, id: string, label: string): ViewRecord {
  return { type, id, label };
}

/**
 * One badge per record — the one that changes the answer (app-shell ADR-014). Ordered by
 * how much it changes it: a blocker outranks an orphan, which outranks liveness.
 */
function ticketNote(ticket: Ticket, link: WorkItemSessionLink | undefined): string | undefined {
  if (!ticket.project_id) return 'no project';
  if (link?.running) return 'session running';
  if (link) return 'session attached';
  return undefined;
}

export function boardRecords(
  board: BoardViewResponse,
  sessions: Record<string, WorkItemSessionLink>
): SurfaceRecords {
  const counts: ViewLaneCount[] = [];
  const visible: ViewRecord[] = [];
  for (const lane of BOARD_COLUMNS) {
    const tickets = board.columns.find((column) => column.state === lane)?.tickets ?? [];
    counts.push({ lane: TICKET_STATE_LABELS[lane], total: tickets.length });
    for (const ticket of tickets) {
      const note = ticketNote(ticket, sessions[ticket.id]);
      visible.push({
        type: 'ticket',
        id: ticket.id,
        label: `${ticket.identifier} ${ticket.title}`,
        state: TICKET_STATE_LABELS[lane],
        ...(note ? { note } : {}),
      });
    }
  }
  return { counts, visible };
}

export function projectRecords(
  projects: GoalActivityProject[],
  blockersFor: (nodeId: string) => string[] | undefined
): SurfaceRecords {
  const lanes = groupProjectsByLane(projects);
  const counts: ViewLaneCount[] = [];
  const visible: ViewRecord[] = [];
  for (const lane of PROJECT_LANES) {
    counts.push({ lane: PROJECT_LANE_LABELS[lane], total: lanes[lane].length });
    for (const project of lanes[lane]) {
      const blockers = blockersFor(project.id);
      const note =
        blockers && blockers.length > 0
          ? `blocked by ${blockers.join(', ')}`
          : lane === 'unassigned'
            ? 'unassigned'
            : undefined;
      visible.push({
        type: 'project',
        id: project.id,
        label: project.name,
        state: PROJECT_LANE_LABELS[lane],
        ...(note ? { note } : {}),
      });
    }
  }
  return { counts, visible };
}

export function initiativeRecords(
  initiatives: GoalActivityInitiative[],
  stageLabelFor: (initiativeId: string) => string
): SurfaceRecords {
  const totals = new Map<string, number>();
  const visible: ViewRecord[] = [];
  for (const initiative of initiatives) {
    const stage = stageLabelFor(initiative.id);
    totals.set(stage, (totals.get(stage) ?? 0) + 1);
    visible.push({
      type: 'initiative',
      id: initiative.id,
      label: initiative.name,
      state: stage,
      note: `${initiative.projects.length} projects`,
    });
  }
  return {
    counts: [...totals].map(([lane, total]) => ({ lane, total })),
    visible,
  };
}

/**
 * The org chart sends EXPANDED nodes only: a collapsed subtree is not on screen,
 * and sending it would misdescribe what the user is looking at.
 */
export function expandedOrgRecords(
  root: OrgTreeNode | null | undefined,
  expandedIds: ReadonlySet<string>
): SurfaceRecords {
  const visible: ViewRecord[] = [];
  const totals = new Map<string, number>();

  const walk = (node: OrgTreeNode) => {
    const role = ROLE_DISPLAY_NAMES[node.position.role];
    totals.set(role, (totals.get(role) ?? 0) + 1);
    const occupant = node.position.user_id
      ? 'occupied by a person'
      : node.position.agent_deleted
        ? `unstaffed: its agent ${occupantAgentName(node.position)} was deleted`
        : node.position.agent_id
          ? `occupied by the agent ${occupantAgentName(node.position)}`
          : 'vacant';
    visible.push({
      type: 'position',
      id: node.position.id,
      label: node.position.title,
      state: role,
      note: occupant,
    });
    if (!expandedIds.has(node.position.id)) return;
    for (const child of node.children) walk(child);
  };

  if (root) walk(root);
  return {
    counts: [...totals].map(([lane, total]) => ({ lane, total })),
    visible,
  };
}
